import React, { useEffect, useRef, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import { Link } from "react-router-dom";
import RightArrow from "../../components/SVG";
import toast from "react-hot-toast";
import { createEventRegistration } from "../../services/sapEventServices";
import { CheckCircle, Briefcase } from "lucide-react";
import imgMember from "../../assets/img/wa-sap/Picture1.jpg";
import imgMember1 from "../../assets/img/wa-sap/Picture2.jpg";
import imgMember2 from "../../assets/img/wa-sap/Picture3.jpg";
import imgMember3 from "../../assets/img/wa-sap/Picture4.jpg";
import imgMember4 from "../../assets/img/wa-sap/Picture5.jpg";
import imgMember5 from "../../assets/img/wa-sap/Picture6.jpg";
import imgMember6 from "../../assets/img/wa-sap/Picture7.png";
import mumbai from "../../assets/img/mumbai_1.jpg";
import Session from "../../assets/img/session.jpg";

// Success Component
const SuccessComponent = () => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        padding: "40px",
        backgroundColor: "#f0fdf4",
        borderRadius: "12px",
        border: "2px solid #10b981",
        margin: "40px auto",
        maxWidth: "600px",
      }}
    >
      <CheckCircle
        size={80}
        style={{ color: "#10b981", marginBottom: "24px" }}
      />
      <h2
        style={{
          fontSize: "28px",
          fontWeight: 700,
          color: "#0E2A46",
          marginBottom: "16px",
          textAlign: "center",
        }}
      >
        Registration Successful!
      </h2>
      <p
        style={{
          fontSize: "16px",
          color: "#374151",
          textAlign: "center",
          lineHeight: "1.6",
          marginBottom: "12px",
        }}
      >
        Your event registration has been submitted successfully.
      </p>
      <p
        style={{
          fontSize: "16px",
          color: "#374151",
          textAlign: "center",
          lineHeight: "1.6",
          marginBottom: "24px",
          fontWeight: 600,
        }}
      >
        You will receive a confirmation email shortly.
      </p>
      <Link
        to="/"
        style={{
          padding: "12px 32px",
          backgroundColor: "#0E2A46",
          color: "#fff",
          borderRadius: "8px",
          textDecoration: "none",
          fontWeight: 600,
          transition: "background-color 0.3s",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = "#1e3a5f")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "#0E2A46")
        }
      >
        Go to Home
      </Link>
    </div>
  );
};

const SAPEventRegistration = () => {
  // Form state
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLocation] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [designation, setDesignation] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const sectionRef = useRef(null);

  const scrollToSection = () => {
    if (sectionRef.current) {
      const offset = -150; // 👈 Adjust this (in px) for how much higher you want to stop
      const elementTop =
        sectionRef.current.getBoundingClientRect().top + window.scrollY;
      const targetPosition = elementTop + offset;

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (window.location.hash === "#form") {
      // small timeout ensures layout is rendered first
      setTimeout(() => {
        scrollToSection();
      }, 300);
    }
  }, []);

  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Individual field errors
  const [errors, setErrors] = useState({
    name: "",
    mobile: "",
    email: "",
    location: "",
    companyName: "",
    designation: "",
    jobDescription: "",
  });

  // Validate individual field
  const validateField = (fieldName, value) => {
    let error = "";

    switch (fieldName) {
      case "name":
        if (!value || !value.trim()) {
          error = "Name is required";
        }
        break;
      case "mobile":
        if (!value || !value.trim()) {
          error = "Mobile number is required";
        } else if (value.length !== 10) {
          error = "Mobile number must be exactly 10 digits";
        }
        break;
    }

    setErrors((prev) => ({ ...prev, [fieldName]: error }));
    return error === "";
  };

  const handleMobileChange = (value) => {
    const cleanedValue = value.replace(/\D/g, "");
    if (cleanedValue.length <= 10) {
      setMobile(cleanedValue);
      validateField("mobile", cleanedValue);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create new errors object to collect all validation errors
    const newErrors = {
      name: "",
      mobile: "",
    };

    let isValid = true;

    // Validate all fields
    if (!name || !name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!mobile || !mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
      isValid = false;
    } else if (mobile.length !== 10) {
      newErrors.mobile = "Mobile number must be exactly 10 digits";
      isValid = false;
    }

    // Update all errors at once
    setErrors(newErrors);

    if (!isValid) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: name.trim(),
        mobile: mobile.trim(),
        email: email.trim().toLowerCase(),
        location: location.trim(),
        companyName: companyName.trim(),
        designation: designation.trim(),
      };

      // Make the actual API call
      const response = await createEventRegistration(payload);

      console.log("Event Registration Response:", response);

      setIsSubmitted(true);
      toast.success(
        "Registration successful! You will receive a confirmation email shortly."
      );
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Reset form
      setName("");
      setMobile("");
      setEmail("");
      setLocation("");
      setCompanyName("");
      setDesignation("");
      setErrors({
        name: "",
        mobile: "",
        email: "",
        location: "",
        companyName: "",
        designation: "",
      });
    } catch (err) {
      console.error("Event registration error:", err);

      // Handle specific error messages from backend
      const errorMessage =
        err?.message || "Registration failed. Please try again.";

      if (errorMessage.includes("Email already registered")) {
        setErrors((prev) => ({ ...prev, email: errorMessage }));
        toast.error("This email is already registered for the event");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <main>
        <Breadcrumb title="WAS - SAP Event Registration Form" />
        <div className="it-student-area it-instructor-style">
          <div className="container">
            <SuccessComponent />
          </div>
        </div>
      </main>
    );
  }

  const speakers = [
    {
      name: "Saji Chacko",
      img: imgMember,
      title: "President, Society of Aquaculture Professionals (SAP)",
      location: "Chennai, India",
      topic: "Bird's Eye View of the Indian Shrimp Aquaculture Sector",
      bio: "Mr. Saji Chacko is a seasoned aquaculture professional with over three decades of experience in shrimp farming, industry development, and sectoral leadership. A postgraduate in Mariculture, he was among the first to start shrimp farming in Gujarat and later pioneered the introduction of Litopenaeus vannamei in the state. He also worked in Kenya, managing large-scale shrimp and finfish operations, gaining valuable international exposure to tropical aquaculture systems. Recognized by MPEDA for innovation and excellence, he has consistently promoted science-based and sustainable practices. As President of the Society of Aquaculture Professionals (SAP), Mr. Chacko remains committed to farmer empowerment, technological advancement, and responsible growth—shaping India’s aquaculture journey within the global blue economy.",
    },
    {
      name: "Dr. A. V. Suresh",
      img: imgMember1,
      title: "Past President, Society of Aquaculture Professionals (SAP)",
      location: "Chennai, India",
      topic: "Advancements in Shrimp Nutrition and Feed Production",
      bio: "Dr Victor Suresh commenced his career in the aqua feed industry at Ralston Purina International and has provided technical services to many feed companies in the Americas and Asia for the last 29 years. He serves as the Technial Director for Growel Feeds in India. He has been closely associated with Bentoli, a feed additive manufacturer, for nearly 25 years. His areas of expertise include feed formulation, new product development and testing, and feed additives.  He is a founding member of the Society of Aquaculture Professionals and has served the association in various capacities including as its President between 2020 and 2023.",
    },
    {
      name: "D. Ramraj",
      img: imgMember2,
      title: "Past President, Society of Aquaculture Professionals (SAP)",
      location: "Chennai, India",
      topic: "Evolution and the Future of Indian Shrimp Hatcheries",
      bio: "Ramraj Dhamodaraswamy is a post graduate in Mariculture from ICAR. Beginning his career as a biologist in a Shrimp farm in 1989, he has been involved in Shrimp Hatchery operations, Farming and Molecular Diagnostics services in a 3 decade long career that has been concurrent with the growth of Shrimp Industry in India. He has Served as the president of the Society of Aquaculture Professionals (SAP, 2008-10) and the All India Shrimp Hatcheries Association (AISHA 2018-21). Ramraj was an early ACC Certifier Auditor for Best Aquaculture Practices,. He has served on the Governing body of the National Fisheries Development Board (NFDB) and the National Knowledge commission of the government of India. His current Business interest interests include ownership of a Shrimp Hatchery producing SPF P.monodon PL, A Tilapia Hatchery and qPCR diagnostic services for Aquaculture and Veterinary sectors. He is currently a member of the USSEC’s Regional Advisory Council, Soy Excellence Centre, India",
    },
    {
      name: "Ravikumar Yellanki",
      img: imgMember3,
      title: "Past President, Society of Aquaculture Professionals (SAP)",
      location: "Chennai, India",
      topic:
        "Reset. Rethink. Reposition: India's Shrimp Industry in the Age of Trade Barriers",
      bio: "Ravi Kumar Yellanki is a leading figure in India’s shrimp aquaculture industry with over two decades of experience in hatchery operations and aquaculture management. As Managing Director of Vaisakhi Marine Resources Pvt. Ltd., he has been instrumental in promoting bio-secure hatchery practices and quality seed production. A management graduate from Xavier Institute of Management, Bhubaneswar, he also serves as President of the All India Shrimp Hatcheries Association and is the immediate past President of the Society of Aquaculture Professionals. He is recognized for his leadership in advancing sustainable aquaculture and repositioning India’s shrimp industry for future growth",
    },
    {
      name: "Aditya Dash",
      img: imgMember4,
      title: "Managing Director, Ram's Assorted Cold Storage",
      location: "Suryo Udyog Ltd",
      topic: "Pond to Pixel - Reimagining Aquaculture Processing",
      bio: "Aditya Dash is the Managing Director of Suryo Udyog Ltd, division of the Suryo group of companies. The Suryo group has been in the seafood industry for more than 40 years and owns a vertically integrated aquaculture value chain for shrimp. They have a shrimp hatchery, corporate shrimp farms and processing units in Odisha. They have also recently launched a seafood sourcing company called Ayana Seafood which helps companies in EU and USA to source seafood products from India. Mr. Dash graduated from the George Washington University, Washington DC in 2006. He has also completed a certificate degree in sustainable aquaculture from University of St. Andrews, Scotland. He has an active interest in public policy and has completed a graduate certificate course in public policy from the Takshashila Institute, Bangalore. Mr. Dash was Vice Chairman of MPEDA, India’s trade promotion body for the exports of marine products. He continues to be an Authority Member of MPEDA. In his spare time, Mr. Dash likes to read about History and Mythology. He also likes to Stand Up Paddle",
    },
    {
      name: "Dr. Srinivasa Rao Patchala",
      img: imgMember5,
      title: "Senior Technical Manager, Avanti Feeds Ltd",
      location: "Avanti Feeds Ltd",
      topic: "Sustainable and resilient shrimp farming practices of India",
      bio: "Dr. Srinivasa Rao Patchala is a specialized aquaculture professional with over two decades of experience in technical marketing, having built deep expertise in shrimp farming systems and shrimp disease management. He holds advanced academic qualifications, including a PhD in Fish Biotechnology and a Master's in Fish Pathology from the Central Institute of Fisheries Education (CIFE), Mumbai. This educational background has positioned him as an expert whose counsel is sought across the industry.Currently serving as the Senior Technical Manager at Avanti Feeds Ltd. (since 2012), Dr. Srinivas’s primary role is to provide comprehensive extension services and technical solutions to farmers, addressing issues across the entire production cycle—from pond preparation to harvest. A key focus of his work involves collaborating with Channel Partners to enhance sustainability in shrimp farming and promoting the adoption of new technology to improve overall production efficiency. His commitment to the industry is further evidenced by his role as an EC member in SAP (Society for Aquaculture Professionals).",
    },
  ];

  const moderator = {
    name: "Dr. S. Santhana Krishnan",
    title: "Founder President, SAP",
  };

  return (
    <main>
      {/* <Breadcrumb title="WAS - SAP Event Registration Form" /> */}
      <div className="relative text-white overflow-hidden flex items-center mb-10">
        {/* Decorative Elements */}
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${mumbai})` }}
        ></div>
        <div className="absolute inset-0 bg-black/70"></div>

        <div className="absolute top-20 right-10 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="relative container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto text-center">
            <img src={Session} className="w-full object-cover object-top" />

            <p className="text-lg md:text-xl text-white mb-6 mt-20 max-w-3xl mx-auto font-light">
              The Society of Aquaculture Professionals (SAP), a supporting
              partner of World Aquaculture 2025, will host a breakout session on
              12 November 2025 at the Novotel Convention Center, Hyderabad. The
              four-hour session, titled “Bird’s Eye View of the Indian Shrimp
              Aquaculture Sector,” will focus on advancements in nutrition,
              challenges in the hatchery sector, innovation in farming,
              repositioning Indian shrimp in global markets, and the future of
              processing.
            </p>
            {/* <div className="grid  !text-white md:grid-cols-3 gap-3 mb-6 max-w-3xl mx-auto">
              <div className="p-3 bg-white/10  backdrop-blur-md rounded-xl border border-white/20">
                <Calendar className="w-6 h-6 text-teal-300 mx-auto mb-2" />
                <p className="text-xs text-blue-200 mb-1">Event Dates</p>
                <p className="font-semibold !text-white">Nov 10-13, 2025</p>
              </div>

              <div className="p-3 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                <MapPin className="w-6 h-6 text-teal-300 mx-auto mb-2" />
                <p className="text-xs text-blue-200 mb-1">Location</p>
                <p className="font-semibold !text-white">Hyderabad, India</p>
              </div>

              <div className="p-3 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                <Clock className="w-6 h-6 text-teal-300 mx-auto mb-2" />
                <p className="text-xs text-blue-200 mb-1">Venue</p>
                <p className="font-semibold !text-white">Novotel HICC</p>
              </div>
            </div> */}
            {/* <div className="max-w-3xl mx-auto mb-6">
              <div className="p-5 bg-gradient-to-r from-white/15 to-white/5 backdrop-blur-lg rounded-2xl border border-white/30 shadow-2xl">
                <div className="w-max mx-auto mb-2 px-3 py-1 bg-teal-500/30 rounded-full border border-teal-300/50">
                  <span className="text-xs font-semibold text-teal-100">
                    Featured Session
                  </span>
                </div>
                <h2 className="text-xl text-white md:text-2xl font-bold mb-3">
                  Bird's Eye View of the Indian Shrimp Aquaculture Sector
                </h2>
                <div className="flex flex-wrap justify-center gap-4 text-xs text-blue-200">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>November 12, 2025</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>14:20 - 18:00 PM IST</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-semibold text-teal-300">
                      SAP Breakout Session 12
                    </span>
                  </div>
                </div>
              </div>
            </div> */}

            {/* <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={scrollToSection}
                className="px-6 py-3 bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
              >
                Register Now
              </button>
            </div> */}
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-b from-white via-gray-50 to-white pt-40 ">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              SAP Breakout Session
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Session Speakers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Industry leaders and pioneers shaping the future of Indian
              aquaculture
            </p>
          </div>

          {/* Speakers Grid - 2 Columns */}
          <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6 mb-12">
            {speakers.map((speaker, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                {/* Photo Section */}
                <div className="h-56 bg-gradient-to-br from-blue-500 to-teal-500 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full h-28 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <img
                        src={speaker.img}
                        className="rounded-full w-[150px] h-[150px] object-cover object-top"
                      />
                    </div>
                  </div>
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-blue-600">
                    Speaker #{index + 1}
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {speaker.name}
                  </h3>
                  <div className="flex items-start gap-2 mb-4">
                    <Briefcase className="w-4 h-4 text-blue-600 flex-shrink-0 mt-1" />
                    <p className="text-sm text-gray-600 font-medium leading-tight">
                      {speaker.title}
                    </p>
                  </div>

                  {/* Topic */}
                  <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r">
                    <p className="text-sm font-bold text-blue-900 leading-snug">
                      {speaker.topic}
                    </p>
                  </div>

                  {/* Bio */}
                  <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                    {speaker.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Moderator Section */}
          <div className="max-w-7xl mx-auto mb-20">
            <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl shadow-xl overflow-hidden">
              <div className="flex flex-col  items-center p-8 gap-6">
                <div className="w-full h-28 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <img
                    src={imgMember6}
                    className="rounded-full w-[150px] h-[150px] object-cover object-top"
                  />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold text-white mb-2">
                    Session Moderator
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-1">
                    {moderator.name}
                  </h3>
                  <p className="text-blue-100 text-lg">{moderator.title}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    
    </main>
  );
};

export default SAPEventRegistration;
